import {
  type DutyRules,
  type FhbInput,
  FhbInputSchema,
  type FhbOutput,
  type FhbRuleSet,
  type FhbScheme,
  type FhbState,
  type FhogRules,
  type SchemeVerdict,
} from "./types.js";

// ──────────────────────────── The C2 hard rule ────────────────────────────
//
// BINDING (regulatory C2 — the panel's CRITICAL): the schemes are government
// programs, but the downstream MORTGAGE is NCCP-regulated credit, and *credit
// assistance* is a licensed activity (ACL/NCCP Act). A personalised eligibility
// result combined with a paid broker/lender/loan handoff is the credit kill-line.
//
// So we encode it as code, not prose: ANY surface that displays a personalised
// `schemeEligibility` result is forbidden from also carrying a credit handoff.
// `schemeEligibility` calls `assertNoCreditHandoff` on the surface descriptor it
// is rendered into; a credit-shaped placement throws before a result is produced.
// Permitted: non-credit, non-personalised footer/resources placements only
// (conveyancer, building/pest inspection). There is a test for this guard.

/** Placement kinds that may appear ALONGSIDE a personalised result surface. */
export const ALLOWED_RESULT_PLACEMENTS = [
  "conveyancer", // non-credit professional referral
  "building_pest_inspection", // non-credit professional referral
  "scheme_official_link", // links to the govt scheme's own page
  "state_revenue_office_link", // "verify with <office>" link
] as const;
export type AllowedPlacement = (typeof ALLOWED_RESULT_PLACEMENTS)[number];

/**
 * Placement kinds that are FORBIDDEN on any personalised-result surface because
 * they are (or front) NCCP-regulated credit / credit assistance. Listed
 * explicitly so the guard is auditable and the test can enumerate them.
 */
export const FORBIDDEN_CREDIT_PLACEMENTS = [
  "mortgage_broker",
  "lender",
  "loan_product",
  "home_loan",
  "pre_approval",
  "get_pre_approved",
  "compare_loans",
  "credit_assistance",
] as const;
export type ForbiddenPlacement = (typeof FORBIDDEN_CREDIT_PLACEMENTS)[number];

const FORBIDDEN_SET: ReadonlySet<string> = new Set(FORBIDDEN_CREDIT_PLACEMENTS);

/** Thrown when a credit handoff is attached to a personalised-result surface. */
export class CreditHandoffError extends Error {
  constructor(placement: string) {
    super(
      `C2 violation: '${placement}' is an NCCP credit handoff and must not appear ` +
        `on a personalised first-home-scheme eligibility surface. Credit assistance is ` +
        `a licensed activity; only non-credit placements (${ALLOWED_RESULT_PLACEMENTS.join(", ")}) ` +
        `are permitted alongside a personalised result.`,
    );
    this.name = "CreditHandoffError";
  }
}

/**
 * Guard the result surface: throws `CreditHandoffError` if ANY attached
 * placement is a credit / loan / broker / lender handoff. Call this with the
 * surface's placements before rendering a personalised result. Pure (no I/O).
 */
export function assertNoCreditHandoff(placements: readonly string[]): void {
  for (const p of placements) {
    const key = p.toLowerCase().replace(/[\s-]+/g, "_");
    if (FORBIDDEN_SET.has(key)) throw new CreditHandoffError(p);
  }
}

// ──────────────────────────── Period resolution ────────────────────────────

/**
 * Pick the rule period in force on an ISO assessment date. Pure string
 * comparison (ISO sorts lexically). `periods` should be newest-first; the first
 * whose `[effectiveFrom, effectiveTo]` window contains the date wins.
 */
export function fhbPeriodForDate(
  isoDate: string,
  periods: readonly FhbRuleSet[],
): FhbRuleSet {
  for (const p of periods) {
    const afterStart = isoDate >= p.effectiveFrom;
    const beforeEnd = p.effectiveTo === null || isoDate <= p.effectiveTo;
    if (afterStart && beforeEnd) return p;
  }
  throw new Error(`no first-home-scheme rule period registered for date ${isoDate}`);
}

// ──────────────────────── Per-scheme gate evaluation ────────────────────────

/** The capital-city / region price-cap group for a state (v1 mapping). */
function priceCapGroupFor(state: FhbState): string {
  switch (state) {
    case "NSW":
      return "Sydney/Newcastle/Illawarra";
    case "VIC":
      return "Melbourne/Geelong";
    case "QLD":
      return "Brisbane";
  }
}

/** 1. Australian Government 5% Deposit Scheme (federal). */
function fivePercentVerdict(input: FhbInput, rules: FhbRuleSet): SchemeVerdict {
  const fp = rules.fivePercent;
  const reasons: string[] = [];
  let eligible = true;

  if (!input.firstHomeBuyer) {
    eligible = false;
    reasons.push("The scheme is for first home buyers only.");
  }

  const minDeposit = input.singleParentOrGuardian
    ? fp.singleParentMinDeposit
    : fp.minDeposit;
  if (input.depositFraction < minDeposit) {
    eligible = false;
    reasons.push(
      `Your deposit of ${(input.depositFraction * 100).toFixed(1)}% is below the ` +
        `${(minDeposit * 100).toFixed(0)}% minimum for this stream.`,
    );
  } else {
    reasons.push(
      `Your deposit meets the ${(minDeposit * 100).toFixed(0)}% minimum (no Lenders ` +
        `Mortgage Insurance under the scheme).`,
    );
  }

  const group = priceCapGroupFor(input.state);
  const cap = fp.priceCaps[group];
  if (cap !== undefined) {
    if (input.price > cap) {
      eligible = false;
      reasons.push(
        `The price exceeds the ${group} price cap of $${cap.toLocaleString("en-AU")}.`,
      );
    } else {
      reasons.push(
        `The price is within the ${group} price cap of $${cap.toLocaleString("en-AU")}.`,
      );
    }
  }

  // Income gate only applied BEFORE 1 Oct 2025; after removal it never fails.
  if (!fp.incomeCapsRemoved) {
    const cap2 = fp.incomeCapSingle;
    if (cap2 !== null && input.income > cap2) {
      eligible = false;
      reasons.push(
        `Your income exceeds the single income cap of $${cap2.toLocaleString("en-AU")} ` +
          `that applied before 1 October 2025.`,
      );
    }
  } else {
    reasons.push("Income caps were removed from 1 October 2025.");
  }

  return {
    scheme: "five_percent_deposit",
    eligible,
    reasons,
    maxBenefit: eligible ? { kind: "lmi_waiver" } : null,
  };
}

/** 2. First Home Super Saver (federal, super) — information only. */
function fhssVerdict(input: FhbInput, rules: FhbRuleSet): SchemeVerdict {
  // FHSS eligibility hinges on being a first home buyer who has not previously
  // owned property; it is income/price/state/build-agnostic. We STATE that it
  // exists and the buyer appears eligible — never advise a release.
  const eligible = input.firstHomeBuyer;
  return {
    scheme: "fhss",
    eligible,
    reasons: eligible
      ? [
          `As a first home buyer you appear eligible to apply to release up to ` +
            `$${rules.fhss.maxReleasable.toLocaleString("en-AU")} of eligible voluntary ` +
            `super contributions under the First Home Super Saver scheme. This tool does ` +
            `not advise whether to use it — see the ATO page and your own circumstances.`,
        ]
      : ["The First Home Super Saver scheme is for first home buyers only."],
    maxBenefit: eligible ? { kind: "super_release" } : null,
  };
}

/** 3. State First Home Owner Grant. */
function fhogVerdict(input: FhbInput, fhog: FhogRules): SchemeVerdict {
  const reasons: string[] = [];
  let eligible = true;

  if (!input.firstHomeBuyer) {
    eligible = false;
    reasons.push("The grant is for first home buyers only.");
  }
  if (fhog.newBuildOnly && input.buildType !== "new") {
    eligible = false;
    reasons.push(
      "The First Home Owner Grant applies to new builds only — an established " +
        "home receives $0.",
    );
  }
  if (fhog.valueCap !== null && input.price > fhog.valueCap) {
    eligible = false;
    reasons.push(
      `The price exceeds the grant value cap of $${fhog.valueCap.toLocaleString("en-AU")}.`,
    );
  }
  if (eligible) {
    reasons.push(
      `A new build within the value cap qualifies for the grant (a gated maximum ` +
        `of $${fhog.amount.toLocaleString("en-AU")}).`,
    );
  }

  return {
    scheme: "fhog",
    eligible,
    reasons,
    maxBenefit: eligible ? { kind: "grant", amount: fhog.amount } : null,
  };
}

/** 4. State stamp-duty exemption / concession. */
function dutyVerdict(input: FhbInput, duty: DutyRules): SchemeVerdict {
  const reasons: string[] = [];

  // QLD new-build branch: nil duty regardless of value from 1 May 2025.
  if (duty.newBuildExemptRegardlessOfValue && input.buildType === "new") {
    return {
      scheme: "stamp_duty_relief",
      eligible: true,
      reasons: [
        "A new build qualifies for full stamp-duty exemption regardless of value " +
          "(Queensland, from 1 May 2025).",
      ],
      maxBenefit: { kind: "duty_exemption", level: "full" },
    };
  }

  if (input.price <= duty.fullExemptionCeiling) {
    reasons.push(
      `The price is at or below the full-exemption ceiling of ` +
        `$${duty.fullExemptionCeiling.toLocaleString("en-AU")} — nil duty.`,
    );
    return {
      scheme: "stamp_duty_relief",
      eligible: true,
      reasons,
      maxBenefit: { kind: "duty_exemption", level: "full" },
    };
  }
  if (input.price <= duty.concessionCeiling) {
    reasons.push(
      `The price falls in the tapered-concession band ` +
        `($${duty.fullExemptionCeiling.toLocaleString("en-AU")}–` +
        `$${duty.concessionCeiling.toLocaleString("en-AU")}) — a partial concession.`,
    );
    return {
      scheme: "stamp_duty_relief",
      eligible: true,
      reasons,
      maxBenefit: { kind: "duty_exemption", level: "concession" },
    };
  }
  reasons.push(
    `The price exceeds the concession ceiling of ` +
      `$${duty.concessionCeiling.toLocaleString("en-AU")} — no first-home duty relief.`,
  );
  return { scheme: "stamp_duty_relief", eligible: false, reasons, maxBenefit: null };
}

// ────────────────────────── Cross-scheme stacking ──────────────────────────

/**
 * The cross-scheme interaction gotchas no single government checker surfaces.
 * This is the moat — neutrality + state resolution + stacking awareness.
 */
function stackingNotes(input: FhbInput, rules: FhbRuleSet, verdicts: SchemeVerdict[]): string[] {
  const notes: string[] = [];
  const eligible = new Set(verdicts.filter((v) => v.eligible).map((v) => v.scheme));

  // Gotcha A — 5%-scheme price cap exceeds the duty-exemption ceiling.
  const group = priceCapGroupFor(input.state);
  const fpCap = rules.fivePercent.priceCaps[group];
  const dutyCeiling = rules.duty[input.state].fullExemptionCeiling;
  if (fpCap !== undefined && fpCap > dutyCeiling) {
    notes.push(
      `The 5% Deposit Scheme price cap ($${fpCap.toLocaleString("en-AU")}) is well above ` +
        `the ${input.state} full stamp-duty exemption ceiling ` +
        `($${dutyCeiling.toLocaleString("en-AU")}): a price between the two can use the ` +
        `deposit scheme yet still owe (reduced or full) duty.`,
    );
  }

  // Gotcha B — new-vs-established FHOG = $0 on an established home.
  if (input.buildType === "established" && rules.fhog[input.state].newBuildOnly) {
    notes.push(
      "Buying an established home means the First Home Owner Grant pays $0 — the grant " +
        "is new-build only in this state.",
    );
  }

  // Gotcha C — QLD new build: $0 FHOG cap test vs unlimited duty exemption.
  if (input.state === "QLD" && input.buildType === "new") {
    notes.push(
      "In Queensland a NEW build gets full stamp-duty exemption regardless of value " +
        "(from 1 May 2025), but the First Home Owner Grant still has its own value cap — " +
        "the two reliefs do not share a ceiling.",
    );
  }

  // Stack summary — additive (grant + duty + LMI-avoidance + FHSS), not tapering.
  if (eligible.size > 1) {
    notes.push(
      "These schemes stack: the grant, the stamp-duty relief, the LMI saving and the " +
        "super release each apply on their own gates and do not reduce one another.",
    );
  }
  return notes;
}

/**
 * `schemeEligibility` — the first-home-scheme MAP. From an assessment date +
 * inputs (state × income × deposit × build × price) + the resolved ruleset,
 * return which of the four schemes the buyer appears eligible for and how they
 * stack. Pure: same inputs + ruleset always yield the same output; validates at
 * the boundary.
 *
 * NOTE the C2 guard: pass the result surface's placements via `surfacePlacements`
 * and they are checked BEFORE any result is produced — a credit/broker/lender
 * handoff throws `CreditHandoffError`. Default is no placements (clean surface).
 */
export function schemeEligibility(
  assessmentDate: string,
  rawInput: FhbInput,
  rules: FhbRuleSet,
  surfacePlacements: readonly string[] = [],
): FhbOutput {
  // Hard rule first — a credit handoff must never reach a personalised result.
  assertNoCreditHandoff(surfacePlacements);

  const input = FhbInputSchema.parse(rawInput);

  const verdicts: SchemeVerdict[] = [
    fivePercentVerdict(input, rules),
    fhssVerdict(input, rules),
    fhogVerdict(input, rules.fhog[input.state]),
    dutyVerdict(input, rules.duty[input.state]),
  ];

  const order: FhbScheme[] = [
    "five_percent_deposit",
    "fhss",
    "fhog",
    "stamp_duty_relief",
  ];
  const eligibleSchemes = order.filter(
    (s) => verdicts.find((v) => v.scheme === s)?.eligible,
  );

  return {
    assessmentDate,
    periodLabel: rules.periodLabel,
    verdicts,
    eligibleSchemes,
    stackingNotes: stackingNotes(input, rules, verdicts),
  };
}
